import React from 'react';
import { Modal, Button, CropImage, Message } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fileExtensionsToAccept } from '../../../nacre/uploadButton/stateless/mimeTypes';
import { useDropzone } from 'react-dropzone';
import Style from './modalEditImage.module.css';

let imageUrl = '';

export default function ModalEditImage({
    isOpen = false,
    subtitle = null,
    isSubmitting = false,
    ratio = null,
    forceRatio = false,
    minWidth = 180,
    maxWidth = Infinity,
    minHeight = 180,
    maxHeight = Infinity,
    onClose = () => {},
    imageFile = null,
    onSubmit = () => {},
    onSelectFile = () => {},
    fileTypes = [],
    maxSize = 5242880,
    fileErrors = [],
    maxPixels = 2400 * 2400
}) {
    const { t } = useTranslation();

    const [imageCroppedFile, setImageCroppedFile] = React.useState(null);

    const errors = {
        'file-invalid-type': t('ticket.event.dropzone_error_file_invalid_type'),
        'file-too-large': t('ticket.event.dropzone_error_file_too_large', { maxsize: Math.trunc(maxSize / 1000000) }),
        'file-too-small': t('ticket.event.dropzone_error_file_too_small'),
        'too-many-files': t('ticket.event.dropzone_error_too_many_files'),
        'image-pixels-too-large': t('ticket.event.dropzone_error_image_pixels_too_large', { maxPixels: Math.sqrt(maxPixels) })
    };

    const imgSrc = React.useMemo(() => {
        if (imageUrl) {
            URL.revokeObjectURL(imageUrl);
        }
        if (imageFile) {
            imageUrl = URL.createObjectURL(imageFile);
            return imageUrl;
        }

        return null;
    }, [imageFile]);

    const acceptedTypes = React.useMemo(() => {
        return fileExtensionsToAccept(fileTypes);
    }, [fileTypes]);

    const { getInputProps, open } = useDropzone({
        accept: acceptedTypes,
        onDrop: onSelectFile,
        multiple: false,
        maxSize: maxSize
    });

    const _onSubmit = React.useCallback(() => {
        onSubmit(imageCroppedFile);
    }, [onSubmit, imageCroppedFile]);

    const _onChangeCrop = React.useCallback((blobUrl, file) => {
        setImageCroppedFile(file);
    }, []);

    return (
        <Modal
            size="extralarge"
            allowExternalClosing={false}
            open={isOpen}
            onClose={onClose}
            title={t('ticket.event.modalEditImage.title')}
            subtitle={subtitle}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} disabled={isSubmitting} label={t('ticket.common.action.cancel')} />
                    <Button primary inverted onClick={open} disabled={isSubmitting} label={t('ticket.event.modalEditImage.changeImageLabel')} />
                    <Button disabled={!imageCroppedFile || fileErrors.length} isLoading={isSubmitting} primary onClick={_onSubmit} label={t('ticket.common.action.save')} />
                </Button.Group>
            }
        >
            {fileErrors.map(error => {
                return (
                    <Message type="danger" title="" className={Style['modal-edit-image__file-error']} key={error}>
                        {errors[error]}
                    </Message>
                );
            })}
            <input {...getInputProps()} />
            <CropImage
                key={imgSrc}
                imgSrc={imgSrc}
                imgTypeMime={imageFile?.type}
                onChange={_onChangeCrop}
                ratio={ratio}
                forceRatio={forceRatio}
                minWidth={minWidth}
                maxWidth={maxWidth}
                minHeight={minHeight}
                maxHeight={maxHeight}
                messageTitleRatio={t('ticket.modal.crop_info.title')}
                messageTextRatio={t('ticket.modal.crop_info.description')}
                labelRatio={t('ticket.common.ratio')}
                labelWidth={t('ticket.common.width')}
                labelHeight={t('ticket.common.height')}
                labelActions={t('ticket.common.actions')}
            />
        </Modal>
    );
}
